import * as yup from 'yup';

import { BannerDestinationType, BannerSlot } from '../types/bannerManagement';
import { scheduleDefaultValues, scheduleSchema } from './scheduleSchema';

const bannerImageFieldSchema = yup.object({
  preview: yup.string().required('A imagem é obrigatória.'),
  file: yup.mixed().optional(),
});

const bannerOptionalImageFieldSchema = yup.object({
  preview: yup.string().optional(),
  file: yup.mixed().optional(),
});

const bannerTopImagesSchema = yup.object({
  web: bannerImageFieldSchema.required('Envie a imagem Web.'),
  tablet: bannerImageFieldSchema.required('Envie a imagem Tablet.'),
  mobile: bannerImageFieldSchema.required('Envie a imagem Mobile.'),
  single: bannerOptionalImageFieldSchema.optional(),
});

const bannerMiddleOrBottomImagesSchema = yup.object({
  web: bannerOptionalImageFieldSchema.optional(),
  tablet: bannerOptionalImageFieldSchema.optional(),
  mobile: bannerOptionalImageFieldSchema.optional(),
  single: bannerImageFieldSchema.required('Faça o upload da imagem.'),
});

const bannerDefaultImagesSchema = yup.object({
  web: bannerOptionalImageFieldSchema.optional(),
  tablet: bannerOptionalImageFieldSchema.optional(),
  mobile: bannerOptionalImageFieldSchema.optional(),
  single: bannerOptionalImageFieldSchema.optional(),
});

const bannerBaseContentSchema = yup.object({
  name: yup.string().trim().required('Escreva o nome do banner.'),
  destinationType: yup
    .mixed<BannerDestinationType>()
    .oneOf(
      Object.values(BannerDestinationType),
      'Selecione o destino do banner.'
    )
    .required('Selecione o destino do banner.'),
  destinationLink: yup.string().when('destinationType', {
    is: BannerDestinationType.Link,
    then: schema =>
      schema
        .trim()
        .required('Informe o link de destino.')
        .url('Informe uma URL válida.'),
    otherwise: schema => schema.nullable().optional(),
  }),
  destinationProduct: yup.string().when('destinationType', {
    is: BannerDestinationType.Product,
    then: schema => schema.required('Selecione um produto.'),
    otherwise: schema => schema.nullable().optional(),
  }),
  destinationCategory: yup.string().when('destinationType', {
    is: BannerDestinationType.Category,
    then: schema => schema.required('Selecione uma categoria.'),
    otherwise: schema => schema.nullable().optional(),
  }),
});

const bannerTopContentSchema = bannerBaseContentSchema.shape({
  images: bannerTopImagesSchema,
});

const bannerMiddleOrBottomContentSchema = bannerBaseContentSchema.shape({
  images: bannerMiddleOrBottomImagesSchema,
});

const bannerDefaultContentSchema = bannerBaseContentSchema.shape({
  images: bannerDefaultImagesSchema,
});

const resolveBannerContentSchemaBySlot = (slot?: BannerSlot | null) => {
  if (slot === BannerSlot.Top) {
    return bannerTopContentSchema;
  }

  if (slot === BannerSlot.Middle || slot === BannerSlot.Bottom) {
    return bannerMiddleOrBottomContentSchema;
  }

  return bannerDefaultContentSchema;
};

export const bannerConfigurationSchema = yup.object({
  platform: yup
    .string()
    .required('Selecione onde o banner será exibido (Web ou App).'),
  segment: yup.string().required('Selecione o público do banner.'),
  slotType: yup
    .string()
    .required('Selecione a posição do banner (Topo, Meio ou Fundo).'),
});

export const bannerContentSchema = bannerDefaultContentSchema;

export const bannerScheduleSchema = scheduleSchema.shape({
  isActive: yup.boolean().optional(),
  withoutEndDate: yup.boolean().optional(),
});

export const bannerRootValidationSchema = yup.object({
  configuration: bannerConfigurationSchema,
  content: yup.lazy((_, options) => {
    const slot = (
      options?.parent as
        | { configuration?: { slot?: BannerSlot | null } }
        | undefined
    )?.configuration?.slot;

    return resolveBannerContentSchemaBySlot(slot);
  }),
  schedule: bannerScheduleSchema,
});

export type BannerConfigurationFormData = yup.InferType<
  typeof bannerConfigurationSchema
>;

export type BannerContentFormData = yup.InferType<typeof bannerContentSchema>;

export type BannerRootData = yup.InferType<typeof bannerRootValidationSchema>;

export const bannerRootDefaultValues: BannerRootData = {
  configuration: {
    platform: '',
    segment: '',
    slotType: '',
  } as any,
  content: {
    name: '',
    images: {
      web: { preview: '', file: undefined },
      tablet: { preview: '', file: undefined },
      mobile: { preview: '', file: undefined },
      single: { preview: '', file: undefined },
    },
    destinationType: '' as BannerDestinationType,
    destinationLink: '',
    destinationProduct: '',
    destinationCategory: '',
  },
  schedule: {
    ...scheduleDefaultValues,
    publishType: 'scheduled',
    isActive: false,
    withoutEndDate: false,
  },
};
